import { Fragment } from 'react'
import Flex from 'components/priority/Flex/Flex'
import Text from 'components/priority/Text/Text'
import {
  AdditionalFamilyInput,
  CaseDateWrapper,
  CaseInputWrapper,
} from 'components/dashboard/CaseDetails/CaseInputWrappers'
import { CaseInputSelector } from 'components/dashboard/CaseDetails'
import caseForm from 'utils/forms/caseForm'
import { formFieldKeys, caseFormSectionsLabels } from '@messenger/consts'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'

// M3-only case fields surfaced in MDL surfaces (Edit Case modal + editor
// sidebar) so an M3 order can be personalized without switching back to the
// m3 modal. Mirrors the m3 Deceased Info sections; genealogy slots mirror
// MIP RelationTypeEnum 7-18. MDL orders never render these components.

type GenealogyRow =
  | { kind: 'subheader'; label: string }
  | { kind: 'field'; key: string; label: string }

type GenealogySide = { header: string; rows: GenealogyRow[] }

const GENEALOGY_SIDES: GenealogySide[] = [
  {
    header: caseFormSectionsLabels.mothersSide,
    rows: [
      {
        kind: 'field',
        key: formFieldKeys.maternalGrandmother,
        label: caseFormSectionsLabels.grandmother,
      },
      {
        kind: 'subheader',
        label: caseFormSectionsLabels.grandmothersParents,
      },
      {
        kind: 'field',
        key: formFieldKeys.maternalGrandmothersMother,
        label: caseFormSectionsLabels.greatGrandmother,
      },
      {
        kind: 'field',
        key: formFieldKeys.maternalGrandmothersFather,
        label: caseFormSectionsLabels.greatGrandfather,
      },
      {
        kind: 'field',
        key: formFieldKeys.maternalGrandfather,
        label: caseFormSectionsLabels.grandfather,
      },
      {
        kind: 'subheader',
        label: caseFormSectionsLabels.grandfathersParents,
      },
      {
        kind: 'field',
        key: formFieldKeys.maternalGrandfathersMother,
        label: caseFormSectionsLabels.greatGrandmother,
      },
      {
        kind: 'field',
        key: formFieldKeys.maternalGrandfathersFather,
        label: caseFormSectionsLabels.greatGrandfather,
      },
    ],
  },
  {
    header: caseFormSectionsLabels.fathersSide,
    rows: [
      {
        kind: 'field',
        key: formFieldKeys.paternalGrandmother,
        label: caseFormSectionsLabels.grandmother,
      },
      {
        kind: 'subheader',
        label: caseFormSectionsLabels.grandmothersParents,
      },
      {
        kind: 'field',
        key: formFieldKeys.paternalGrandmothersMother,
        label: caseFormSectionsLabels.greatGrandmother,
      },
      {
        kind: 'field',
        key: formFieldKeys.paternalGrandmothersFather,
        label: caseFormSectionsLabels.greatGrandfather,
      },
      {
        kind: 'field',
        key: formFieldKeys.paternalGrandfather,
        label: caseFormSectionsLabels.grandfather,
      },
      {
        kind: 'subheader',
        label: caseFormSectionsLabels.grandfathersParents,
      },
      {
        kind: 'field',
        key: formFieldKeys.paternalGrandfathersMother,
        label: caseFormSectionsLabels.greatGrandmother,
      },
      {
        kind: 'field',
        key: formFieldKeys.paternalGrandfathersFather,
        label: caseFormSectionsLabels.greatGrandfather,
      },
    ],
  },
]

const sectionLabel = (t: TFunction, label: string): string =>
  t(`caseFormSectionsLabels.${label}`, { defaultValue: label })

// Great-grandparents sit under a "…'s parents" sub-header; indent them so the
// generational hierarchy is easy to follow.
const isGreatGrandparentRow = (row: GenealogyRow): boolean =>
  row.kind === 'field' &&
  (row.label === caseFormSectionsLabels.greatGrandmother ||
    row.label === caseFormSectionsLabels.greatGrandfather)

export const M3DateOfMarriageField = () => {
  const { t } = useTranslation()
  return (
    <CaseDateWrapper
      width="100%"
      margin="0 24px 24px 24px"
      label={sectionLabel(t, caseFormSectionsLabels.dateOfMarriage)}
      name={formFieldKeys.dateOfMarriage}
    />
  )
}

/**
 * The familyInfo fields with date of marriage spliced in directly below the
 * spouse input (matching the m3 modal), driven by the same caseForm config
 * the MDL section uses so the two stay in sync.
 */
export const M3FamilyInfoFields = () => (
  <Flex wrap="wrap" width="100%">
    {caseForm.familyInfo.map((formField, index) => (
      <Fragment key={`${formField.key}_${index}`}>
        <CaseInputSelector formField={formField} />
        {formField.key === formFieldKeys.spouse && <M3DateOfMarriageField />}
      </Fragment>
    ))}
  </Flex>
)

/**
 * The 12 genealogy inputs grouped by side — no wrapper, for embedding.
 *
 * Must stay a row-wrap flex (the CaseFormSection / M3FamilyInfoFields
 * pattern): inputs declare width 100% + horizontal margins and rely on
 * flex-shrink to absorb the margins, keeping the indented
 * great-grandparent fields flush with every other field.
 */
export const M3GenealogyFields = () => {
  const { t } = useTranslation()
  return (
    <Flex wrap="wrap" width="100%">
      {GENEALOGY_SIDES.map((side) => (
        <Fragment key={side.header}>
          <Flex full>
            <Text size="14px" weight="600" margin="0 24px 24px 24px">
              {sectionLabel(t, side.header)}
            </Text>
          </Flex>
          {side.rows.map((row, index) =>
            row.kind === 'subheader' ? (
              <Flex full key={`${row.label}_${index}`}>
                <Text size="12px" weight="600" margin="0 24px 16px 24px">
                  {sectionLabel(t, row.label)}
                </Text>
              </Flex>
            ) : (
              <CaseInputWrapper
                key={row.key}
                width="100%"
                margin={
                  isGreatGrandparentRow(row)
                    ? '0 24px 24px 48px'
                    : '0 24px 24px 24px'
                }
                label={sectionLabel(t, row.label)}
                name={row.key}
              />
            ),
          )}
        </Fragment>
      ))}
    </Flex>
  )
}

/**
 * Flat variants for the editor sidebar: plain section title + fields,
 * matching how the sidebar renders every other case section
 * (CaseFormSection sidebar mode).
 */
const SidebarSectionTitle = ({ label }: { label: string }) => {
  const { t } = useTranslation()
  return (
    <Flex full>
      <Text size="16px" weight="600" margin="0 24px 24px 24px">
        {sectionLabel(t, label)}
      </Text>
    </Flex>
  )
}

export const M3AdditionalFamilySection = () => (
  <Flex wrap="wrap" width="100%">
    <SidebarSectionTitle label={caseFormSectionsLabels.additionalFamily} />
    <AdditionalFamilyInput width="100%" margin="0 24px 24px 24px" />
  </Flex>
)

export const M3GenealogySection = () => (
  <Flex wrap="wrap" width="100%">
    <SidebarSectionTitle label={caseFormSectionsLabels.genealogy} />
    <M3GenealogyFields />
  </Flex>
)
